import * as React from "react";
import { ChevronDown, Eye, FileText, FolderOpen, Trash2 } from "lucide-react";
import type { Expense, SourceDocument } from "@/types/tax";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { PdfPreview } from "@/components/domain/PdfPreview";
import { documentPeriodDate, getBasPeriodForDate } from "@/lib/gst";
import { cn, currency } from "@/lib/utils";

const documentKindLabels: Record<string, string> = {
  income_summary: "Income summaries",
  expense_bill: "Expense bills",
  unknown: "Other documents",
};

function getDocumentKindLabel(document: SourceDocument) {
  return documentKindLabels[document.documentKind || "unknown"] || documentKindLabels.unknown;
}

function getLinkedExpense(document: SourceDocument, expenses: Expense[]) {
  return expenses.find((expense) => expense.sourceDocumentId === document.id);
}

function getDocumentDate(document: SourceDocument, expenses: Expense[]) {
  const linkedExpense = getLinkedExpense(document, expenses);
  if (linkedExpense?.date) return linkedExpense.date;
  return documentPeriodDate(document);
}

function getDocumentSummary(document: SourceDocument, expenses: Expense[]) {
  const linkedExpense = getLinkedExpense(document, expenses);
  if (linkedExpense) {
    return {
      primary: `${linkedExpense.date} · ${linkedExpense.supplier || "Expense bill"}`,
      secondary: `Expense ${currency(linkedExpense.amount)}`,
      emphasis: true,
    };
  }

  if (document.extraction) {
    return {
      primary: `${document.extraction.periodStart || "Unknown"} to ${document.extraction.periodEnd || "Unknown"}`,
      secondary: `Gross fares ${currency(document.extraction.grossFares)}`,
      emphasis: true,
    };
  }

  return {
    primary: `Uploaded ${document.uploadedAt.slice(0, 10)}`,
    secondary: "Accepted records are saved separately.",
    emphasis: false,
  };
}

function groupDocuments(documents: SourceDocument[], expenses: Expense[]) {
  const grouped = new Map<
    string,
    {
      id: string;
      label: string;
      dateKey: string;
      documents: SourceDocument[];
      counts: {
        stored: number;
        needsReview: number;
      };
    }
  >();

  documents.forEach((document) => {
    const dateKey = getDocumentDate(document, expenses);
    const period = getBasPeriodForDate(dateKey);
    const existing = grouped.get(period.id);
    const group = existing || {
      id: period.id,
      label: period.label,
      dateKey: period.endDate,
      documents: [],
      counts: {
        stored: 0,
        needsReview: 0,
      },
    };

    group.documents.push(document);
    if (document.storagePath) group.counts.stored += 1;
    if (document.status === "needs_review") group.counts.needsReview += 1;
    grouped.set(period.id, group);
  });

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      documents: group.documents.sort((a, b) => getDocumentDate(b, expenses).localeCompare(getDocumentDate(a, expenses)) || b.uploadedAt.localeCompare(a.uploadedAt)),
    }))
    .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
}

export function DocumentsTable({ documents, expenses, onRemovePdf }: { documents: SourceDocument[]; expenses: Expense[]; onRemovePdf: (id: string) => void }) {
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const groups = React.useMemo(() => groupDocuments(documents, expenses), [documents, expenses]);
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(() => new Set());

  React.useEffect(() => {
    if (previewId && !documents.some((document) => document.id === previewId && document.storagePath)) {
      setPreviewId(null);
    }
  }, [documents, previewId]);

  React.useEffect(() => {
    setExpandedGroups((current) => {
      let changed = false;
      const next = new Set<string>();
      const groupIds = new Set(groups.map((group) => group.id));

      current.forEach((id) => {
        if (groupIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [groups]);

  function toggleGroup(groupId: string) {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Source documents</CardTitle>
        <CardDescription>PDFs are kept as source evidence until deleted. Accepted income and expense records remain separate calculation data.</CardDescription>
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface p-6 text-center">
            <p className="font-semibold text-ink">No documents yet</p>
            <p className="mt-1 text-sm text-muted">Pro scans appear here after a PDF is uploaded.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {groups.map((group) => {
              const expanded = expandedGroups.has(group.id);
              const typeCounts = group.documents.reduce<Record<string, number>>((counts, document) => {
                const label = getDocumentKindLabel(document);
                counts[label] = (counts[label] || 0) + 1;
                return counts;
              }, {});

              return (
                <section key={group.id} className="overflow-hidden rounded-lg border border-border bg-surface">
                  <button
                    type="button"
                    className="flex min-h-16 w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-surface-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    aria-expanded={expanded}
                    aria-controls={`documents-${group.id}`}
                    onClick={() => {
                      if (expanded && group.documents.some((document) => document.id === previewId)) {
                        setPreviewId(null);
                      }
                      toggleGroup(group.id);
                    }}
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FolderOpen className="size-5" aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-semibold text-ink">{group.label}</span>
                        <span className="mt-1 block text-sm text-muted">
                          {group.documents.length} {group.documents.length === 1 ? "document" : "documents"}
                          {group.counts.stored > 0 ? ` | ${group.counts.stored} stored PDF${group.counts.stored === 1 ? "" : "s"}` : ""}
                        </span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {group.counts.needsReview > 0 ? <Badge variant="payable">{group.counts.needsReview} review</Badge> : null}
                      <ChevronDown className={cn("size-5 text-muted transition", expanded ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
                    </span>
                  </button>

                  {expanded ? (
                    <div id={`documents-${group.id}`} className="border-t border-border bg-white p-2 sm:p-3">
                      <div className="mb-2 flex flex-wrap gap-2">
                        {Object.entries(typeCounts).map(([label, count]) => (
                          <Badge key={label} variant="muted">
                            {label}: {count}
                          </Badge>
                        ))}
                      </div>
                      <div className="grid gap-2">
                        {group.documents.map((document) => {
                          const summary = getDocumentSummary(document, expenses);

                          return (
                            <div key={document.id} className="rounded-lg border border-border bg-white p-3">
                              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                                <div className="flex min-w-0 gap-3">
                                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                    <FileText className="size-4" aria-hidden="true" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex min-w-0 items-center gap-2">
                                      <p className="truncate font-semibold text-ink">{document.fileName}</p>
                                      {document.status !== "accepted" ? (
                                        <Badge variant="payable" className="shrink-0">
                                          {document.status.replace("_", " ")}
                                        </Badge>
                                      ) : null}
                                    </div>
                                    <p className="mt-1 truncate text-sm text-muted">
                                      {summary.primary} ·{" "}
                                      <span className={cn(summary.emphasis ? "tabular font-semibold text-ink" : "")}>{summary.secondary}</span>
                                    </p>
                                    <p className="mt-1 text-xs text-muted">
                                      {getDocumentKindLabel(document)}
                                      {document.storagePath ? " · Source stored" : ""}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 sm:justify-end">
                                  {document.storagePath ? (
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      size="sm"
                                      aria-expanded={previewId === document.id}
                                      onClick={() => setPreviewId((current) => (current === document.id ? null : document.id))}
                                    >
                                      <Eye className="size-4" aria-hidden="true" />
                                      {previewId === document.id ? "Hide" : "Preview"}
                                    </Button>
                                  ) : null}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-danger hover:text-danger"
                                    aria-label={`Delete source file for ${document.fileName}`}
                                    onClick={() => onRemovePdf(document.id)}
                                  >
                                    <Trash2 className="size-4" aria-hidden="true" />
                                    <span className="hidden sm:inline">Delete</span>
                                  </Button>
                                </div>
                              </div>
                              {previewId === document.id && document.storagePath ? (
                                <PdfPreview
                                  documentId={document.id}
                                  fileName={document.fileName}
                                  className="mt-4"
                                  autoOpen
                                  onClose={() => setPreviewId(null)}
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
